/**
 * v3 API 基础请求层
 *
 * 封装统一的 fetch 请求逻辑、响应解析和错误处理。
 * 适配后端 v3 统一响应格式：
 *   成功: { success: true, data: ..., timestamp: "..." }
 *   失败: { success: false, error: { code: "...", message: "..." }, timestamp: "..." }
 *
 * @module services/api/v3/client
 */

const API_BASE_URL = 'https://api2.ororigin.xyz';

/** 默认请求头 */
const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
};

// ============================================================================
// 自定义错误
// ============================================================================

/**
 * v3 API 错误
 *
 * 当后端返回 success: false 时抛出，携带后端返回的 error.code 和 error.message。
 */
export class V3ApiError extends Error {
  /**
   * @param {string} code   - 机器可读错误码，如 "BOT_NOT_FOUND"
   * @param {string} message - 人类可读错误描述
   * @param {number} status  - HTTP 状态码
   */
  constructor(code, message, status) {
    super(message);
    this.name = 'V3ApiError';
    this.code = code;
    this.status = status;
  }
}

// ============================================================================
// 请求核心
// ============================================================================

/**
 * 发送 v3 API 请求
 *
 * 自动拼接 /api/ 前缀并处理统一的 { success, data/error, timestamp } 响应格式。
 * 成功时直接返回 data 字段；失败时抛出 V3ApiError。
 *
 * @param {string}  url    - API 路径（不含 /api 前缀），如 "/bots"
 * @param {object}  [options={}] - fetch 选项，与原生 fetch 相同
 * @returns {Promise<any>} 后端返回的 data 字段内容
 * @throws {V3ApiError} 后端返回 success: false 或网络异常时抛出
 */
async function request(url, options = {}) {
  const fullUrl = `${API_BASE_URL}/api${url}`;

  let response;
  let body;

  // 仅在有 body 时设置 Content-Type，避免空 body POST 请求报错
  const headers = options.body
    ? { ...DEFAULT_HEADERS, ...options.headers }
    : { ...options.headers };

  // ----- 网络层捕获 -----
  try {
    response = await fetch(fullUrl, {
      ...options,
      headers,
    });
  } catch (networkError) {
    throw new V3ApiError(
      'NETWORK_ERROR',
      networkError.message || '网络请求失败',
      0,
    );
  }

  // ----- JSON 解析 -----
  try {
    body = await response.json();
  } catch (parseError) {
    throw new V3ApiError(
      'PARSE_ERROR',
      `响应 JSON 解析失败: ${parseError.message}`,
      response.status,
    );
  }

  // ----- 业务层 success: false -----
  if (body && body.success === false && body.error) {
    throw new V3ApiError(
      body.error.code || 'UNKNOWN_ERROR',
      body.error.message || '未知错误',
      response.status,
    );
  }

  // ----- HTTP 状态码异常（但响应体不是标准 ApiError 格式） -----
  if (!response.ok) {
    throw new V3ApiError(
      'HTTP_ERROR',
      body?.message || `HTTP ${response.status}`,
      response.status,
    );
  }

  // ----- 成功：返回 data 字段 -----
  return body?.data !== undefined ? body.data : body;
}

/**
 * 修改 API base URL（用于多环境切换）
 *
 * @param {string} newBaseUrl - 新的 API 基础地址
 */
export function setApiBaseUrl(newBaseUrl) {
  // eslint-disable-next-line no-import-assign
  // API_BASE_URL = newBaseUrl;  // 常量不可重新赋值，这里仅作为 API 预留
  // 暂时通过模块闭包变量实现：
  const moduleUrl = newBaseUrl;
  // 重新导出 request 会使用更新的 URL
  // 注意：当前设计为简单起见，base URL 是模块级常量。
  // 如需运行时切换，可将 base URL 改为对象属性或使用工厂模式。
  console.warn('[v3Api] setApiBaseUrl 暂未实现，当前使用默认:', API_BASE_URL);
}

export default request;
